import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { AppDialog } from './AppDialog';
import { useDialogA11y } from '../hooks/useDialogA11y';
import { GoalService, GoalTemplate } from '../services/goalService';
import { ResidentGoalsService } from '../services/residentGoalsService';
import { CategoryService, DashboardCategory } from '../services/categoryService';
import { ProgramSettingsService } from '../services/programSettingsService';
import { GoalActivityService } from '../services/goalActivityService';
import { CredentialsService } from '../services/credentialsService';
import './GoalsTracker.css';

interface Goal {
  id: string;
  careDailyGoalId?: string | null;
  title: string;
  description?: string;
  category: string;
  categoryId?: string | null;
  completed: boolean;
  completedAt?: string | null;
}

interface WellnessDimension {
  id: string;
  name: string;
  icon: string;
  color: string;
}

interface GoalsTrackerProps {
  residentId: string;
  facilityId: string;
  dimensions: WellnessDimension[];
  careDailyLocationId?: string; // CareDaily location ID for sync
  externalId?: string; // External user id (from JWT), used for goalActivity (best-effort)
  isExpanded?: boolean;
  onToggle?: (nextExpanded: boolean) => void;
  /** Wheel-derived hint: the resident's least-active wellness area. Only set when
   *  that dimension links to a goal category, so the suggestion is actionable and
   *  an added goal in the category dismisses it. */
  balanceHint?: { categoryId: string; areaName: string };
  /** Fired whenever the resident's goal set changes after initial load (add,
   *  complete/uncomplete, delete, background sync) so goal-driven consumers
   *  (event recommendations) can re-score without a page reload. */
  onGoalsChanged?: () => void;
}

export const GoalsTracker: React.FC<GoalsTrackerProps> = ({ residentId, facilityId, dimensions, careDailyLocationId, externalId, isExpanded: isExpandedProp, onToggle, balanceHint, onGoalsChanged }) => {
  const [goals, setGoals] = useState<Goal[]>([]);
  const [goalTemplates, setGoalTemplates] = useState<GoalTemplate[]>([]);
  const [categories, setCategories] = useState<DashboardCategory[]>([]);
  const [programName, setProgramName] = useState('Successful Aging');
  const [loading, setLoading] = useState(true);
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const isExpanded = typeof isExpandedProp === 'boolean' ? isExpandedProp : internalIsExpanded;
  const [updatingGoalId, setUpdatingGoalId] = useState<string | null>(null);
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [addingTemplateId, setAddingTemplateId] = useState<string | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [deletingGoalId, setDeletingGoalId] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ goalId: string; goalTitle: string } | null>(null);
  const [messageDialog, setMessageDialog] = useState<{ title: string; description: string } | null>(null);
  const [goalActivityEnvOverride, setGoalActivityEnvOverride] = useState<'sandbox' | 'production' | null>(null);
  const setExpanded = (nextExpanded: boolean) => {
    onToggle?.(nextExpanded);
    if (typeof isExpandedProp !== 'boolean') {
      setInternalIsExpanded(nextExpanded);
    }
  };

  const fetchGoals = useCallback(async (
    syncFirst: boolean = false,
    options?: { background?: boolean }
  ) => {
    if (!residentId || !facilityId) {
      setLoading(false);
      return;
    }

    try {
      const isBackground = options?.background === true;
      if (isBackground) {
        setSyncing(true);
      } else {
        setLoading(true);
      }
      
      // Fetch resident goals from Supabase (optionally sync with CareDaily first)
      const residentGoals = await ResidentGoalsService.getGoals(
        residentId, 
        facilityId, 
        careDailyLocationId,
        syncFirst // Sync with CareDaily if requested
      );
      
      // Map to Goal interface with category names
      const mappedGoals = residentGoals.map(rg => {
        const category = categories.find(cat => cat.id === rg.categoryId);
        return {
          id: rg.id,
          careDailyGoalId: rg.careDailyGoalId || null,
          title: rg.title,
          description: rg.description,
          category: category?.displayName || 'General',
          categoryId: rg.categoryId,
          completed: rg.completed,
          completedAt: rg.completedAt
        };
      });
      
      setGoals(mappedGoals);
    } catch (error) {
      console.error('Failed to fetch goals:', error);
      setGoals([]);
    } finally {
      const isBackground = options?.background === true;
      if (isBackground) {
        setSyncing(false);
      } else {
        setLoading(false);
      }
    }
  }, [residentId, facilityId, careDailyLocationId, categories]);

  const fetchTemplates = useCallback(async () => {
    try {
      const [templatesData, categoriesData, programSettings] = await Promise.all([
        GoalService.getGoalTemplates(facilityId),
        CategoryService.getCategories(facilityId),
        ProgramSettingsService.getProgramSettings(facilityId)
      ]);
      
      setGoalTemplates(templatesData);
      setCategories(categoriesData);
      setProgramName(programSettings.programName);
    } catch (error) {
      console.error('Failed to fetch goal templates:', error);
    }
  }, [facilityId]);

  // Load templates and categories first
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (!facilityId) {
      setGoalActivityEnvOverride(null);
      return;
    }
    let isActive = true;
    CredentialsService.getCredentialStatus(facilityId)
      .then((credentials) => {
        if (!isActive) return;
        if (credentials) {
          setGoalActivityEnvOverride(credentials.isSandbox ? 'sandbox' : 'production');
        } else {
          setGoalActivityEnvOverride(null);
        }
      })
      .catch((error) => {
        console.warn('⚠️ Unable to resolve CareDaily env for goal activity logging.', error);
        if (isActive) {
          setGoalActivityEnvOverride(null);
        }
      });
    return () => {
      isActive = false;
    };
  }, [facilityId]);

  // Load goals after categories are available
  useEffect(() => {
    if (categories.length > 0) {
      // If connected to CareDaily, always sync first on initial load.
      fetchGoals(!!careDailyLocationId);
    }
  }, [categories, fetchGoals, careDailyLocationId]);

  // Prevent background scroll when the history modal is open (keeps wheel/touch scrolling inside the modal).
  useEffect(() => {
    if (!showHistoryModal) return;
    const prevOverflow = document.body.style.overflow;
    const prevOverscroll = (document.body.style as any).overscrollBehavior;
    document.body.style.overflow = 'hidden';
    (document.body.style as any).overscrollBehavior = 'contain';
    return () => {
      document.body.style.overflow = prevOverflow;
      (document.body.style as any).overscrollBehavior = prevOverscroll;
    };
  }, [showHistoryModal]);

  const historyModalRef = useRef<HTMLDivElement>(null);
  useDialogA11y({
    isOpen: showHistoryModal,
    onClose: () => setShowHistoryModal(false),
    dialogRef: historyModalRef,
  });

  // Notify consumers when the goal set actually changes after the initial load.
  // Signature covers id + completed (completing a goal changes the need vector);
  // a background sync that returns identical goals produces the same signature
  // and stays silent. Latest-ref so an inline callback prop can't churn the effect.
  const onGoalsChangedRef = useRef(onGoalsChanged);
  onGoalsChangedRef.current = onGoalsChanged;
  const goalsSignature = goals.map(g => `${g.id}:${g.completed ? 1 : 0}`).join('|');
  const goalsSignatureInitialized = useRef(false);
  useEffect(() => {
    if (loading) return;
    if (!goalsSignatureInitialized.current) {
      goalsSignatureInitialized.current = true;
      return;
    }
    onGoalsChangedRef.current?.();
  }, [goalsSignature, loading]);

  // Build GoIcon/CareDaily goalActivity payload fields
  const getCategoryNameForGoal = (categoryId?: string | null, fallback?: string) => {
    const match = categoryId ? categories.find(c => c.id === categoryId) : undefined;
    return (match?.displayName || fallback || 'General').trim();
  };

  // Automatic background sync from CareDaily (if connected).
  // - periodic refresh
  // - refresh on tab focus/visibility
  useEffect(() => {
    if (!careDailyLocationId) return;
    if (!residentId || !facilityId) return;
    if (categories.length === 0) return;

    const runBackgroundSync = () => {
      if (document.visibilityState !== 'visible') return;
      // Avoid stacking background sync calls
      if (syncing) return;
      fetchGoals(true, { background: true });
    };

    // Sync on focus/visibility changes
    window.addEventListener('focus', runBackgroundSync);
    document.addEventListener('visibilitychange', runBackgroundSync);

    // Periodic sync (5 minutes)
    const intervalId = window.setInterval(runBackgroundSync, 5 * 60 * 1000);

    return () => {
      window.removeEventListener('focus', runBackgroundSync);
      document.removeEventListener('visibilitychange', runBackgroundSync);
      window.clearInterval(intervalId);
    };
  }, [careDailyLocationId, residentId, facilityId, categories.length, fetchGoals, syncing]);

  /**
   * Re-sync goals after giving the CareDaily bot a moment to update location state.
   * Linked residents should reflect bot-processed state, not optimistic local writes.
   */
  const resyncGoalsFromCareDaily = useCallback(async (delayMs: number = 4000) => {
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, delayMs);
    });
    await fetchGoals(true, { background: true });
  }, [fetchGoals]);

  const handleToggleGoal = async (goalId: string) => {
    setUpdatingGoalId(goalId);
    
    try {
      const goal = goals.find(g => g.id === goalId);
      if (!goal) return;
      
      const newCompleted = !goal.completed;

      if (careDailyLocationId) {
        // Linked resident: optimistic update first, then fire goalActivity and background resync.
        setGoals(prevGoals =>
          prevGoals.map(g =>
            g.id === goalId
              ? { ...g, completed: newCompleted, completedAt: newCompleted ? new Date().toISOString() : null }
              : g
          )
        );
        const residentIdNum = parseInt(careDailyLocationId, 10);
        const externalIdNum = externalId ? parseInt(externalId, 10) : NaN;
        const cdGoalId = goal.careDailyGoalId || undefined;
        const categoryName = getCategoryNameForGoal(goal.categoryId, goal.category).toLowerCase();
        await GoalActivityService.submit({
          ResidentId: residentIdNum,
          ExternalId: Number.isFinite(externalIdNum) ? externalIdNum : 0,
          Action: newCompleted ? 'complete' : 'reopen',
          goal: {
            id: cdGoalId,
            goal_id: cdGoalId,
            title: goal.title,
            description: goal.description,
            category: categoryName,
            completed: newCompleted,
          },
          timestamp: Date.now(),
        }, { envOverride: goalActivityEnvOverride || undefined });
        // Background reconcile — does not block the UI
        resyncGoalsFromCareDaily(4000).catch(e => console.warn('⚠️ Background goal resync failed:', e));
      } else {
        // Unlinked resident: Supabase only.
        await ResidentGoalsService.toggleGoalCompletion(goalId, newCompleted);
        setGoals(prevGoals =>
          prevGoals.map(g =>
            g.id === goalId
              ? { ...g, completed: newCompleted, completedAt: newCompleted ? new Date().toISOString() : null }
              : g
          )
        );
      }
    } catch (error) {
      console.error('Failed to update goal:', error);
      setMessageDialog({
        title: 'Unable to update goal',
        description: error instanceof Error ? error.message : 'Failed to update goal. Please try again.'
      });
    } finally {
      setUpdatingGoalId(null);
    }
  };

  const requestDeleteGoal = (goalId: string, goalTitle: string) => {
    setDeleteConfirm({ goalId, goalTitle });
  };

  const confirmDeleteGoal = async () => {
    if (!deleteConfirm) return;

    const { goalId } = deleteConfirm;
    setDeletingGoalId(goalId);
    
    try {
      const deletedGoal = goals.find(g => g.id === goalId);

      if (careDailyLocationId) {
        // Linked resident: optimistic remove first, then fire goalActivity and background resync.
        setGoals(prevGoals => prevGoals.filter(g => g.id !== goalId));
        setDeleteConfirm(null);
        const residentIdNum = parseInt(careDailyLocationId, 10);
        const externalIdNum = externalId ? parseInt(externalId, 10) : NaN;
        const cdGoalId = deletedGoal?.careDailyGoalId || undefined;
        const categoryName = (deletedGoal
          ? getCategoryNameForGoal(deletedGoal.categoryId, deletedGoal.category)
          : 'General').toLowerCase();
        await GoalActivityService.submit({
          ResidentId: residentIdNum,
          ExternalId: Number.isFinite(externalIdNum) ? externalIdNum : 0,
          Action: 'remove',
          goal: deletedGoal
            ? {
                id: cdGoalId,
                goal_id: cdGoalId,
                title: deletedGoal.title,
                description: deletedGoal.description,
                category: categoryName,
              }
            : { goal_id: undefined, id: goalId },
          timestamp: Date.now(),
        }, { envOverride: goalActivityEnvOverride || undefined });
        // Background reconcile — does not block the UI
        resyncGoalsFromCareDaily(4000).catch(e => console.warn('⚠️ Background goal resync failed:', e));
      } else {
        // Unlinked resident: Supabase only.
        await ResidentGoalsService.deleteGoal(goalId);
        setGoals(prevGoals => prevGoals.filter(g => g.id !== goalId));
        setDeleteConfirm(null);
      }
    } catch (error) {
      console.error('Failed to delete goal:', error);
      setMessageDialog({
        title: 'Unable to delete goal',
        description: error instanceof Error ? error.message : 'Failed to delete goal. Please try again.'
      });
    } finally {
      setDeletingGoalId(null);
    }
  };

  const handleAddGoalFromTemplate = async (template: GoalTemplate) => {
    setAddingTemplateId(template.id);
    try {
      const category = categories.find(cat => cat.id === template.categoryId);
      const categoryName = category?.displayName || 'General';
      const categoryKey = categoryName.toLowerCase();

      if (careDailyLocationId) {
        // Linked resident: optimistic add first, then fire goalActivity and background resync.
        const residentIdNum = parseInt(careDailyLocationId, 10);
        const externalIdNum = externalId ? parseInt(externalId, 10) : NaN;
        const newGoalId = `goal-${crypto.randomUUID()}`;
        const now = Date.now();
        // Show goal immediately in UI
        setGoals(prevGoals => [{
          id: newGoalId,
          careDailyGoalId: newGoalId,
          title: template.title,
          description: template.description,
          category: categoryName,
          categoryId: template.categoryId,
          completed: false,
          completedAt: null,
        }, ...prevGoals]);
        await GoalActivityService.submit({
          ResidentId: residentIdNum,
          ExternalId: Number.isFinite(externalIdNum) ? externalIdNum : 0,
          Action: 'add',
          goal: {
            goal_id: newGoalId,
            title: template.title,
            description: template.description,
            category: categoryKey,
            completed: false,
            created_timestamp_ms: now,
            updated_timestamp_ms: now,
          },
          timestamp: now,
        }, { envOverride: goalActivityEnvOverride || undefined });
        // Background reconcile — does not block the UI
        resyncGoalsFromCareDaily(6000).catch(e => console.warn('⚠️ Background goal resync failed:', e));
      } else {
        // Unlinked resident: Supabase only.
        const newGoal = await ResidentGoalsService.createGoal(
          residentId,
          facilityId,
          { title: template.title, description: template.description, categoryId: template.categoryId }
        );
        const mappedGoal: Goal = {
          id: newGoal.id,
          careDailyGoalId: null,
          title: newGoal.title,
          description: newGoal.description,
          category: categoryName,
          categoryId: newGoal.categoryId,
          completed: false,
          completedAt: null
        };
        setGoals(prevGoals => [mappedGoal, ...prevGoals]);
      }

      setShowAddDropdown(false);
    } catch (error) {
      console.error('Failed to add goal:', error);
      setMessageDialog({
        title: 'Unable to add goal',
        description: error instanceof Error ? error.message : 'Failed to add goal. Please try again.'
      });
    } finally {
      setAddingTemplateId(null);
    }
  };

  const getCategoryIcon = (category: string, categoryId?: string | null): string => {
    // Prefer direct lookup via categoryId against dashboard_categories
    if (categoryId) {
      const cat = categories.find(c => c.id === categoryId);
      if (cat?.icon) return cat.icon;
    }
    // Fallback: exact match category display name against dashboard_categories
    const catByName = categories.find(c => c.displayName === category);
    if (catByName?.icon) return catByName.icon;
    // Last resort: match against wellness dimensions
    const dimension = dimensions.find(d => d.name === category);
    return dimension?.icon || 'FaBullseye';
  };

  const getCategoryColor = (category: string, categoryId?: string | null): string => {
    if (categoryId) {
      const cat = categories.find(c => c.id === categoryId);
      if (cat?.color) return cat.color;
    }
    const catByName = categories.find(c => c.displayName === category);
    if (catByName?.color) return catByName.color;
    const dimension = dimensions.find(d => d.name === category);
    return dimension?.color || '#667eea';
  };

  const getCategoryInfo = (categoryId?: string): DashboardCategory | undefined => {
    if (!categoryId) return undefined;
    return categories.find(cat => cat.id === categoryId);
  };

  const activeGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed).sort((a, b) => {
    // Sort by completedAt date, most recent first
    const dateA = a.completedAt ? new Date(a.completedAt).getTime() : 0;
    const dateB = b.completedAt ? new Date(b.completedAt).getTime() : 0;
    return dateB - dateA;
  });
  
  // Show only 3 most recent completed goals in main view
  const recentCompletedGoals = completedGoals.slice(0, 3);
  const hasMoreCompletedGoals = completedGoals.length > 3;

  // Filter out templates that are already ACTIVE in the user's goal list.
  // Completed goals should not block a user from adding the same goal again.
  const availableTemplates = goalTemplates.filter(
    template => !activeGoals.some(goal => goal.title === template.title)
  );

  // Group templates by category
  const groupedTemplates = availableTemplates.reduce((acc, template) => {
    const category = getCategoryInfo(template.categoryId);
    const categoryName = category?.displayName || 'General';
    
    if (!acc[categoryName]) {
      acc[categoryName] = {
        category: category,
        templates: []
      };
    }
    acc[categoryName].templates.push(template);
    return acc;
  }, {} as Record<string, { category?: DashboardCategory; templates: GoalTemplate[] }>);

  if (loading) {
    return (
      <div className="goals-tracker">
        <div className="goals-tracker-header">
          <div className="goals-tracker-title-row">
            <div>
              <h3>My Goals</h3>
              <p className="goals-tracker-subtitle">Track your wellness objectives</p>
            </div>
          </div>
        </div>
        <div className="goals-loading">
          <div className="skeleton skeleton-line skeleton-title"></div>
          <div className="goals-skeleton-list">
            <div className="skeleton skeleton-card"></div>
            <div className="skeleton skeleton-card"></div>
            <div className="skeleton skeleton-card"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="goals-tracker">
      <div className="goals-tracker-header">
        <div 
          className="goals-tracker-title-row"
          onClick={() => setExpanded(!isExpanded)}
        >
          <div>
            <h3>Personalized {programName} Goals</h3>
            <p className="goals-tracker-subtitle">
              {activeGoals.length} active, {completedGoals.length} completed
            </p>
          </div>
          <div className="goals-header-actions">
            {isExpanded && (
              <div className="add-goal-dropdown-container">
                <button
                  type="button"
                  className="add-goal-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (goalTemplates.length === 0) return;
                    setShowAddDropdown(!showAddDropdown);
                  }}
                  aria-label="Add a goal"
                  disabled={goalTemplates.length === 0 || !!addingTemplateId}
                  title={goalTemplates.length === 0 ? 'No goal templates configured' : 'Add a goal'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span>Add Goal</span>
                </button>
                
                {showAddDropdown && createPortal(
                  <div 
                    className="dropdown-backdrop"
                    onClick={() => setShowAddDropdown(false)}
                  >
                    <div className="add-goal-dropdown" onClick={(e) => e.stopPropagation()}>
                      <div className="dropdown-header">
                        <span>Select a Goal</span>
                        <button
                          type="button"
                          className="close-dropdown"
                          onClick={() => setShowAddDropdown(false)}
                          aria-label="Close dropdown"
                        >
                          ×
                        </button>
                      </div>
                      <div className="dropdown-list">
                        {goalTemplates.length === 0 ? (
                          <div className="dropdown-empty">
                            <span className="empty-icon"><Icon name="FaBullseye" size={40} decorative /></span>
                            <p>No goals available.</p>
                            <p className="empty-subtitle">Ask an admin to configure goal templates for this facility.</p>
                          </div>
                        ) : Object.keys(groupedTemplates).length === 0 ? (
                          <div className="dropdown-empty">
                            <span className="empty-icon"><Icon name="FaBullseye" size={40} decorative /></span>
                            <p>All available goals are already active.</p>
                            <p className="empty-subtitle">Complete a goal to add it again.</p>
                          </div>
                        ) : (
                          Object.entries(groupedTemplates).map(([categoryName, { category, templates }]) => (
                            <div key={categoryName} className="dropdown-category-group">
                              <div 
                                className="dropdown-category-header"
                                style={category ? {
                                  backgroundColor: `${category.color}15`,
                                  borderLeftColor: category.color
                                } : undefined}
                              >
                                {category && category.icon && (
                                  <Icon name={category.icon} size={16} />
                                )}
                                <span className="category-name">{categoryName}</span>
                                <span className="category-count">{templates.length}</span>
                              </div>
                              <div className="dropdown-category-items">
                                {templates.map((template) => (
                                  <button
                                    key={template.id}
                                    type="button"
                                    className="dropdown-item"
                                    onClick={() => handleAddGoalFromTemplate(template)}
                                    disabled={!!addingTemplateId}
                                  >
                                    <div className="dropdown-item-content">
                                      <span className="dropdown-item-title">{template.title}</span>
                                    </div>
                                    {template.description && (
                                      <span className="dropdown-item-description">{template.description}</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>,
                  document.body
                )}
              </div>
            )}
            <button
              type="button"
              className={`toggle-button ${isExpanded ? 'expanded' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
              setExpanded(!isExpanded);
              }}
              aria-label={isExpanded ? 'Collapse goals' : 'Expand goals'}
            >
              {isExpanded ? (
                <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              ) : (
                <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Goal Confirmation */}
      <AppDialog
        isOpen={!!deleteConfirm}
        title="Delete goal?"
        description={
          deleteConfirm
            ? `This will permanently delete "${deleteConfirm.goalTitle}". This action cannot be undone.`
            : ''
        }
        onClose={() => setDeleteConfirm(null)}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setDeleteConfirm(null),
          variant: 'secondary'
        }}
        primaryAction={{
          label: deletingGoalId ? 'Deleting…' : 'Delete',
          onClick: confirmDeleteGoal,
          variant: 'danger',
          disabled: !!deletingGoalId
        }}
      />

      {/* Message Dialog (errors, etc.) */}
      <AppDialog
        isOpen={!!messageDialog}
        title={messageDialog?.title || ''}
        description={messageDialog?.description || ''}
        onClose={() => setMessageDialog(null)}
        primaryAction={{
          label: 'OK',
          onClick: () => setMessageDialog(null),
          variant: 'primary'
        }}
      />

      {/* Goal History Modal */}
      {showHistoryModal && createPortal(
        <div className="modal-backdrop" onClick={() => setShowHistoryModal(false)}>
          <div
            ref={historyModalRef}
            tabIndex={-1}
            className="history-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="goal-history-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 id="goal-history-modal-title">Goal History</h3>
              <button
                type="button"
                className="close-modal"
                onClick={() => setShowHistoryModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="history-stats">
                <div className="stat-item">
                  <span className="stat-value">{completedGoals.length}</span>
                  <span className="stat-label">Completed</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{activeGoals.length}</span>
                  <span className="stat-label">Active</span>
                </div>
                <div className="stat-item">
                  <span className="stat-value">{goals.length}</span>
                  <span className="stat-label">Total</span>
                </div>
              </div>
              <div className="history-list">
                {completedGoals.map(goal => (
                  <div key={goal.id} className="history-goal-item">
                    <div className="history-goal-header">
                      <span className="goal-category-icon" style={{ color: getCategoryColor(goal.category, goal.categoryId) }}>
                        <Icon name={getCategoryIcon(goal.category, goal.categoryId)} size={18} decorative />
                      </span>
                      <span className="history-goal-title">{goal.title}</span>
                      <span className="checkmark-badge"><Icon name="FaCheck" size={14} decorative /></span>
                    </div>
                    {goal.description && (
                      <p className="history-goal-description">{goal.description}</p>
                    )}
                    <div className="history-goal-footer">
                      <span 
                        className="goal-category"
                        style={{
                          backgroundColor: `${getCategoryColor(goal.category, goal.categoryId)}18`,
                          color: getCategoryColor(goal.category, goal.categoryId),
                          borderColor: `${getCategoryColor(goal.category, goal.categoryId)}40`
                        }}
                      >
                        <span className="goal-category-badge-icon">
                          <Icon name={getCategoryIcon(goal.category, goal.categoryId)} size={14} decorative />
                        </span>
                        {goal.category}
                      </span>
                      {goal.completedAt && (
                        <span className="goal-completed-date">
                          Completed {new Date(goal.completedAt).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                    <div className="history-goal-actions">
                      <button
                        type="button"
                        className="history-action-button reopen"
                        onClick={() => {
                          handleToggleGoal(goal.id);
                          setShowHistoryModal(false);
                        }}
                        disabled={updatingGoalId === goal.id}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="1 4 1 10 7 10"></polyline>
                          <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"></path>
                        </svg>
                        Reopen
                      </button>
                      <button
                        type="button"
                        className="history-action-button delete"
                        onClick={() => requestDeleteGoal(goal.id, goal.title)}
                        disabled={deletingGoalId === goal.id}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div
        className={`collapsible-content ${isExpanded ? 'expanded' : ''}`}
        aria-hidden={!isExpanded}
      >
        <div className="collapsible-inner">
          <div className="goals-content">
            {balanceHint && !goals.some(g => !g.completed && !!g.categoryId && g.categoryId === balanceHint.categoryId) && (
              <div className="goals-balance-nudge">
                <span>
                  <strong>{balanceHint.areaName}</strong> has the least activity in your wellness
                  mix. Add a {balanceHint.areaName} goal, then check Event Recommendations below for
                  activities that support it.
                </span>
              </div>
            )}
            {goals.length === 0 ? (
              <div className="goals-empty">
                <span className="empty-icon"><Icon name="FaBullseye" size={48} decorative /></span>
                <p>No goals set yet</p>
                <p className="empty-subtitle">Click "Add Goal" to get started</p>
              </div>
            ) : (
              <>
                {/* Active Goals */}
                {activeGoals.length > 0 && (
                  <div className="goals-section">
                    <h4 className="goals-section-title">Active Goals</h4>
                    <div className="goals-list">
                      {activeGoals.map(goal => (
                        <div key={goal.id} className="goal-item">
                          <label className="goal-checkbox-wrapper">
                            <input
                              type="checkbox"
                              checked={goal.completed}
                              onChange={() => handleToggleGoal(goal.id)}
                              disabled={updatingGoalId === goal.id || deletingGoalId === goal.id}
                              className="goal-checkbox"
                              aria-label={`Mark "${goal.title}" as complete`}
                            />
                            <span className="goal-checkmark"></span>
                          </label>
                          
                          <div className="goal-content">
                            <div className="goal-header">
                              <span className="goal-category-icon" style={{ color: getCategoryColor(goal.category, goal.categoryId) }}>
                                <Icon name={getCategoryIcon(goal.category, goal.categoryId)} size={20} decorative />
                              </span>
                              <span className="goal-title">{goal.title}</span>
                            </div>
                            {goal.description && (
                              <p className="goal-description">{goal.description}</p>
                            )}
                            <span
                              className="goal-category"
                              style={{
                                backgroundColor: `${getCategoryColor(goal.category, goal.categoryId)}18`,
                                color: getCategoryColor(goal.category, goal.categoryId),
                                borderColor: `${getCategoryColor(goal.category, goal.categoryId)}40`
                              }}
                            >
                              <span className="goal-category-badge-icon">
                                <Icon name={getCategoryIcon(goal.category, goal.categoryId)} size={14} decorative />
                              </span>
                              {goal.category}
                            </span>
                          </div>

                          <button
                            type="button"
                            className="goal-delete-button"
                            onClick={() => requestDeleteGoal(goal.id, goal.title)}
                            disabled={deletingGoalId === goal.id}
                            aria-label={`Delete "${goal.title}"`}
                            title="Delete goal"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recently Completed Goals (3 most recent) */}
                {recentCompletedGoals.length > 0 && (
                  <div className="goals-section">
                    <div className="goals-section-header">
                      <h4 className="goals-section-title">Recently Completed <Icon name="FaCheck" size={14} style={{ verticalAlign: 'middle' }} /></h4>
                      {hasMoreCompletedGoals && (
                        <button
                          type="button"
                          className="view-history-button"
                          onClick={() => setShowHistoryModal(true)}
                        >
                          View Full History ({completedGoals.length})
                        </button>
                      )}
                    </div>
                    <div className="goals-list">
                      {recentCompletedGoals.map(goal => (
                        <div key={goal.id} className="goal-item completed">
                          <label className="goal-checkbox-wrapper">
                            <input
                              type="checkbox"
                              checked={goal.completed}
                              onChange={() => handleToggleGoal(goal.id)}
                              disabled={updatingGoalId === goal.id || deletingGoalId === goal.id}
                              className="goal-checkbox"
                              aria-label={`Mark "${goal.title}" as incomplete`}
                            />
                            <span className="goal-checkmark"></span>
                          </label>
                          
                          <div className="goal-content">
                            <div className="goal-header">
                              <span className="goal-category-icon" style={{ color: getCategoryColor(goal.category, goal.categoryId) }}>
                                <Icon name={getCategoryIcon(goal.category, goal.categoryId)} size={20} decorative />
                              </span>
                              <span className="goal-title">{goal.title}</span>
                            </div>
                            {goal.description && (
                              <p className="goal-description">{goal.description}</p>
                            )}
                            <div className="goal-footer">
                              <span
                                className="goal-category"
                                style={{
                                  backgroundColor: `${getCategoryColor(goal.category, goal.categoryId)}18`,
                                  color: getCategoryColor(goal.category, goal.categoryId),
                                  borderColor: `${getCategoryColor(goal.category, goal.categoryId)}40`
                                }}
                              >
                                <span className="goal-category-badge-icon">
                                  <Icon name={getCategoryIcon(goal.category, goal.categoryId)} size={14} decorative />
                                </span>
                                {goal.category}
                              </span>
                              {goal.completedAt && (
                                <span className="goal-completed-date">
                                  {new Date(goal.completedAt).toLocaleDateString()}
                                </span>
                              )}
                            </div>
                          </div>

                          <button
                            type="button"
                            className="goal-delete-button"
                            onClick={() => requestDeleteGoal(goal.id, goal.title)}
                            disabled={deletingGoalId === goal.id}
                            aria-label={`Delete "${goal.title}"`}
                            title="Delete goal"
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

    </div>
  );
};

